import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ArrowUpRight, BedDouble, CalendarDays, Download, Image as ImageIcon, Inbox, LogOut, RefreshCw, Settings as SettingsIcon, ShieldCheck, Star, Trash2, Upload } from "lucide-react";
import { Link } from "react-router-dom";
import { API, LOGO_URL, QR_CODE_URL } from "../lib/site";

const CREDS = { withCredentials: true };
const DRAFT_KEY = "madhubhan_admin_drafts_v1";

const STATUS_LABELS = {
  received: "Pending", pending: "Pending", payment_submitted: "UTR Submitted",
  verified: "Confirmed", cancelled: "Cancelled", completed: "Completed",
};
const STATUS_OPTIONS = ["pending", "payment_submitted", "verified", "cancelled", "completed"];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const TABS = [
  { id: "bookings", label: "Bookings", icon: CalendarDays },
  { id: "enquiries", label: "Enquiries", icon: Inbox },
  { id: "rooms", label: "Rooms & Prices", icon: BedDouble },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "settings", label: "Site Settings", icon: SettingsIcon },
  { id: "backup", label: "Backup", icon: ShieldCheck },
];

const EMPTY_ROOM = { name: "", category: "Deluxe", summary: "", guests: "2 guests", bed: "Breakfast for two", rate: "₹0", amenities: "", enabled: true };

export default function Admin() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("bookings");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [settingsForm, setSettingsForm] = useState(null);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [newRoom, setNewRoom] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [drafts, setDrafts] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}"); } catch { return {}; }
  });

  const setDraft = (key, value) => setDrafts((d) => {
    const next = { ...d, [key]: { ...value, _savedAt: new Date().toISOString() } };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(next));
    return next;
  });
  const clearDraft = (key) => setDrafts((d) => {
    const next = { ...d };
    delete next[key];
    localStorage.setItem(DRAFT_KEY, JSON.stringify(next));
    return next;
  });
  const draftCount = Object.keys(drafts).length;

  const loadAll = async () => {
    try {
      const [b, e, r, g, s] = await Promise.all([
        axios.get(`${API}/admin/bookings`, CREDS),
        axios.get(`${API}/admin/enquiries`, CREDS),
        axios.get(`${API}/admin/rooms`, CREDS),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/settings`),
      ]);
      setBookings(b.data);
      setEnquiries(e.data);
      setRooms(r.data);
      setGallery(g.data);
      setSettingsForm(s.data);
    } catch (err) {
      if (err.response?.status === 401) {
        try {
          await axios.post(`${API}/auth/refresh`, {}, CREDS);
          return loadAll();
        } catch { setUser(false); }
      }
    }
  };

  useEffect(() => {
    axios.get(`${API}/auth/me`, CREDS).then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  useEffect(() => { if (user) loadAll(); }, [user]);

  const publishDraft = async (key, silent = false) => {
    const draft = drafts[key];
    if (!draft) return true;
    const payload = { ...draft };
    delete payload._savedAt;
    try {
      if (key === "settings") {
        await axios.put(`${API}/admin/settings`, payload, CREDS);
        setSettingsForm(payload);
      } else if (key.startsWith("room_")) {
        const body = { ...payload, amenities: String(payload.amenities).split(",").map((a) => a.trim()).filter(Boolean) };
        await axios.put(`${API}/admin/rooms/${payload.id}`, body, CREDS);
        setRooms((rs) => rs.map((r) => (r.id === payload.id ? { ...r, ...body } : r)));
      }
      clearDraft(key);
      if (!silent) toast.success("Published — live on the website now");
      return true;
    } catch (err) {
      toast.error("Sync failed", { description: formatApiErrorDetail(err.response?.data?.detail) });
      return false;
    }
  };

  const publishAll = async () => {
    const keys = Object.keys(drafts);
    if (!keys.length) return;
    let failed = 0;
    for (const key of keys) {
      const ok = await publishDraft(key, true);
      if (!ok) failed += 1;
    }
    if (failed) toast.error(`Sync failed for ${failed} change(s) — still saved locally`);
    else toast.success("Sync successful — all changes are live");
    loadAll();
  };

  useEffect(() => {
    const onOnline = () => { if (Object.keys(drafts).length) { toast.info("Internet restored — syncing pending changes…"); publishAll(); } };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [drafts, rooms, settingsForm]);

  const login = async (event) => {
    event.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, loginForm, CREDS);
      setUser(data);
      toast.success(`Welcome back, ${data.name}`);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoggingIn(false); }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, CREDS).catch(() => {});
    setUser(false);
  };

  // ---------- bookings ----------
  const setBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status`, { status }, CREDS);
      setBookings(bookings.map((b) => (b.booking_id === bookingId ? { ...b, status } : b)));
      toast.success(`Status → ${STATUS_LABELS[status]}`);
    } catch { toast.error("Could not update status"); }
  };

  // ---------- rooms ----------
  const selectedRoom = rooms.find((r) => r.id === selectedRoomId);
  const roomDraftKey = selectedRoom ? `room_${selectedRoom.id}` : null;
  const roomBase = selectedRoom ? { ...selectedRoom, amenities: selectedRoom.amenities.join(", ") } : null;
  const effectiveRoom = roomDraftKey && drafts[roomDraftKey] ? drafts[roomDraftKey] : roomBase;

  const publishRoom = async () => {
    setSaving(true);
    await publishDraft(roomDraftKey);
    setSaving(false);
  };

  const toggleRoom = async (room) => {
    try {
      const body = { name: room.name, category: room.category, summary: room.summary, guests: room.guests, bed: room.bed, rate: room.rate, amenities: room.amenities, enabled: !room.enabled };
      await axios.put(`${API}/admin/rooms/${room.id}`, body, CREDS);
      setRooms(rooms.map((r) => (r.id === room.id ? { ...r, enabled: !room.enabled } : r)));
      toast.success(room.enabled ? `${room.name} hidden from website` : `${room.name} is live again`);
    } catch { toast.error("Could not update room"); }
  };

  const moveRoom = async (room, dir) => {
    const idx = rooms.findIndex((r) => r.id === room.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= rooms.length) return;
    const next = [...rooms];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setRooms(next);
    try { await axios.put(`${API}/admin/rooms/reorder`, { ids: next.map((r) => r.id) }, CREDS); }
    catch { toast.error("Order sync failed"); }
  };

  const deleteRoom = async (room) => {
    try {
      await axios.delete(`${API}/admin/rooms/${room.id}`, CREDS);
      setRooms(rooms.filter((r) => r.id !== room.id));
      setSelectedRoomId(null);
      setConfirmDelete(null);
      toast.success(`${room.name} deleted`);
    } catch { toast.error("Could not delete room"); }
  };

  const publishNewRoom = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const body = { ...newRoom, amenities: String(newRoom.amenities).split(",").map((a) => a.trim()).filter(Boolean) };
      const { data } = await axios.post(`${API}/admin/rooms`, body, CREDS);
      setRooms([...rooms, data]);
      setNewRoom(null);
      toast.success(`${data.name} added — now live on the website`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const uploadRoomPhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedRoom) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await axios.post(`${API}/admin/rooms/${selectedRoom.id}/photo`, body, CREDS);
      setRooms(rooms.map((r) => (r.id === selectedRoom.id ? { ...r, image: data.image } : r)));
      toast.success("Photo uploaded — live instantly");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); event.target.value = ""; }
  };

  const removeRoomPhoto = async () => {
    try {
      await axios.delete(`${API}/admin/rooms/${selectedRoom.id}/photo`, CREDS);
      setRooms(rooms.map((r) => (r.id === selectedRoom.id ? { ...r, image: "" } : r)));
      toast.success("Photo removed");
    } catch { toast.error("Could not remove photo"); }
  };

  // ---------- gallery ----------
  const uploadGalleryPhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await axios.post(`${API}/admin/gallery`, body, CREDS);
      setGallery([...gallery, data]);
      toast.success("Photo added to gallery & homepage slider");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); event.target.value = ""; }
  };

  const deleteGalleryPhoto = async (photo) => {
    try {
      await axios.delete(`${API}/admin/gallery/${photo.id}`, CREDS);
      setGallery(gallery.filter((p) => p.id !== photo.id));
      setConfirmDelete(null);
      toast.success("Photo deleted");
    } catch { toast.error("Could not delete photo"); }
  };

  const moveGalleryPhoto = async (photo, dir) => {
    const idx = gallery.findIndex((p) => p.id === photo.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= gallery.length) return;
    const next = [...gallery];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setGallery(next);
    try { await axios.put(`${API}/admin/gallery/reorder`, { ids: next.map((p) => p.id) }, CREDS); }
    catch { toast.error("Order sync failed"); }
  };

  const toggleFeatured = async (photo) => {
    try {
      await axios.put(`${API}/admin/gallery/${photo.id}`, { featured: !photo.featured }, CREDS);
      setGallery(gallery.map((p) => (p.id === photo.id ? { ...p, featured: !photo.featured } : p)));
    } catch { toast.error("Could not update photo"); }
  };

  // ---------- settings ----------
  const eff = drafts.settings || settingsForm;
  const setS = (key) => (event) => setDraft("settings", { ...eff, [key]: event.target.value });
  const setSCheck = (key) => (event) => setDraft("settings", { ...eff, [key]: event.target.checked });
  const publishSettings = async () => { setSaving(true); await publishDraft("settings"); setSaving(false); };

  const uploadQr = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await axios.post(`${API}/admin/qr`, body, CREDS);
      setSettingsForm({ ...settingsForm, qr_image: data.qr_image });
      toast.success("QR code updated — live instantly");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); event.target.value = ""; }
  };

  const deleteQr = async () => {
    try {
      await axios.delete(`${API}/admin/qr`, CREDS);
      setSettingsForm({ ...settingsForm, qr_image: "" });
      toast.success("QR removed — default QR will show");
    } catch { toast.error("Could not remove QR"); }
  };

  // ---------- backup ----------
  const importBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const { data: result } = await axios.post(`${API}/admin/restore`, data, CREDS);
      toast.success("Backup restored", { description: `Settings ✓ · ${result.rooms} rooms · ${result.gallery} gallery photos` });
      loadAll();
    } catch (err) {
      toast.error("Restore failed", { description: "Please choose a valid madhubhan-backup.json file." });
    } finally { event.target.value = ""; }
  };

  if (user === null) {
    return <main className="admin-shell center" data-testid="admin-loading"><p className="muted">Checking your session…</p></main>;
  }

  if (user === false) {
    return (
      <main className="admin-shell center" data-testid="admin-login-page">
        <div className="admin-login admin-card">
          <img className="brand-logo" src={LOGO_URL} alt="Madhubhan Resort & Spa" />
          <h1>Admin sign in</h1>
          <p className="muted">Manage bookings, rooms, prices, photos, payments and website content.</p>
          <form onSubmit={login} data-testid="admin-login-form">
            <label>Email<input type="email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} required placeholder="admin@madhubhanresort.com" data-testid="admin-email" /></label>
            <label>Password<input type="password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} required placeholder="••••••••" data-testid="admin-password" /></label>
            {loginError && <p className="admin-error" data-testid="admin-login-error">{loginError}</p>}
            <button className="primary-button full-width" disabled={loggingIn} type="submit" data-testid="admin-login-submit">
              {loggingIn ? "Signing in…" : "Sign in"} <ArrowUpRight size={16} />
            </button>
          </form>
          <Link to="/" className="text-link admin-back" data-testid="admin-back-home">Back to website</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="admin-shell" data-testid="admin-dashboard">
      <div className="admin-head">
        <div>
          <span className="eyebrow">Madhubhan Resort & Spa</span>
          <h1>Dashboard</h1>
          {draftCount > 0 && <span className="admin-badge" data-testid="draft-badge">Pending sync: {draftCount} offline draft{draftCount > 1 ? "s" : ""}</span>}
        </div>
        <div className="admin-head-actions">
          {draftCount > 0 && <button className="gold-button" onClick={publishAll} data-testid="sync-all">Sync all changes</button>}
          <button className="outline-button" onClick={loadAll} data-testid="admin-refresh"><RefreshCw size={14} /> Refresh</button>
          <Link to="/" className="outline-button" data-testid="admin-view-site">View site</Link>
          <button className="outline-button" onClick={logout} data-testid="admin-logout"><LogOut size={14} /> Sign out</button>
        </div>
      </div>

      <div className="stat-cards">
        <div className="stat-card" data-testid="stat-bookings"><strong>{bookings.length}</strong><span>Booking requests</span></div>
        <div className="stat-card" data-testid="stat-enquiries"><strong>{enquiries.length}</strong><span>Enquiries</span></div>
        <div className="stat-card" data-testid="stat-rooms"><strong>{rooms.length}</strong><span>Rooms & suites</span></div>
      </div>

      <div className="admin-tabs" data-testid="admin-tabs">
        {TABS.map((item) => (
          <button key={item.id} className={tab === item.id ? "filter-chip active" : "filter-chip"} onClick={() => setTab(item.id)} data-testid={`admin-tab-${item.id}`}>
            <item.icon size={14} /> {item.label}
          </button>
        ))}
      </div>

      {tab === "bookings" && (
        <div className="admin-card" data-testid="admin-bookings-panel">
          <div className="admin-card-head">
            <h2>Booking requests</h2>
            <a className="outline-button" href={`${API}/admin/bookings.csv`} data-testid="export-bookings"><Download size={14} /> Download CSV</a>
          </div>
          {bookings.length === 0 && <p className="muted">No booking requests yet.</p>}
          {bookings.map((b) => (
            <div className="admin-row" key={b.booking_id} data-testid={`booking-row-${b.booking_id}`}>
              <div>
                <span className="admin-cell-label">Reference</span><strong>{b.booking_id}</strong>
                <select className="status-select" value={b.status} onChange={(e) => setBookingStatus(b.booking_id, e.target.value)} data-testid={`status-${b.booking_id}`}>
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
              </div>
              <div><span className="admin-cell-label">Guest</span><strong>{b.guest_name}</strong><small>{b.phone} · {b.email}</small></div>
              <div><span className="admin-cell-label">Stay</span><strong>{b.room_type}</strong><small>{b.check_in} → {b.check_out} · {b.guests} guests{b.total_amount ? ` · ₹${b.total_amount.toLocaleString("en-IN")}` : ""}</small></div>
              <div>
                <span className="admin-cell-label">Payment</span>
                <small>{b.payment_method ? `Method: ${b.payment_method.toUpperCase()}` : "Method: —"}<br />{b.utr ? `UTR: ${b.utr}` : "No UTR yet"}</small>
                {b.payment_proof && <a className="text-link" href={b.payment_proof} target="_blank" rel="noreferrer" data-testid={`proof-${b.booking_id}`}>Screenshot</a>}
              </div>
              <div><span className="admin-cell-label">Received</span><small>{new Date(b.created_at).toLocaleString("en-IN")}</small></div>
            </div>
          ))}
        </div>
      )}

      {tab === "enquiries" && (
        <div className="admin-card" data-testid="admin-enquiries-panel">
          <div className="admin-card-head">
            <h2>Enquiries</h2>
            <a className="outline-button" href={`${API}/admin/enquiries.csv`} data-testid="export-enquiries"><Download size={14} /> Download CSV</a>
          </div>
          {enquiries.length === 0 && <p className="muted">No enquiries yet.</p>}
          {enquiries.map((e) => (
            <div className="admin-row" key={e.enquiry_id} data-testid={`enquiry-row-${e.enquiry_id}`}>
              <div><span className="admin-cell-label">Reference</span><strong>{e.enquiry_id}</strong></div>
              <div><span className="admin-cell-label">Name</span><strong>{e.name}</strong><small>{e.phone} · {e.email}</small></div>
              <div className="wide"><span className="admin-cell-label">Message</span><small>{e.message}</small></div>
              <div><span className="admin-cell-label">Received</span><small>{new Date(e.created_at).toLocaleString("en-IN")}</small></div>
            </div>
          ))}
        </div>
      )}

      {tab === "rooms" && (
        <div className="room-editor" data-testid="admin-rooms-panel">
          <div className="admin-card room-list">
            <div className="admin-card-head">
              <h2>Rooms & suites</h2>
              <button className="outline-button" onClick={() => { setNewRoom({ ...EMPTY_ROOM }); setSelectedRoomId(null); }} data-testid="add-room">+ Add room</button>
            </div>
            {rooms.map((room, i) => (
              <div key={room.id} className={selectedRoomId === room.id ? "room-list-item active" : "room-list-item"} data-testid={`admin-room-${room.id}`}>
                <button className="room-list-main" onClick={() => { setSelectedRoomId(room.id); setNewRoom(null); }}>
                  <span>{room.name}{room.enabled === false && <em className="disabled-tag"> hidden</em>}</span>
                  <small>{room.rate} / night</small>
                </button>
                <div className="room-list-actions">
                  <button onClick={() => moveRoom(room, -1)} disabled={i === 0} aria-label="Move up" data-testid={`room-up-${room.id}`}><ArrowUp size={13} /></button>
                  <button onClick={() => moveRoom(room, 1)} disabled={i === rooms.length - 1} aria-label="Move down" data-testid={`room-down-${room.id}`}><ArrowDown size={13} /></button>
                  <button onClick={() => toggleRoom(room)} aria-label="Enable or disable" className={room.enabled === false ? "off" : ""} data-testid={`room-toggle-${room.id}`}>{room.enabled === false ? "Show" : "Hide"}</button>
                </div>
              </div>
            ))}
          </div>
          <div className="admin-card">
            {newRoom ? (
              <form onSubmit={publishNewRoom} data-testid="room-add-form">
                <h2>Add new room</h2>
                <label>Room name<input value={newRoom.name} onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })} required placeholder="e.g. Garden View Room" data-testid="room-add-name" /></label>
                <div className="form-grid">
                  <label>Category
                    <select value={newRoom.category} onChange={(e) => setNewRoom({ ...newRoom, category: e.target.value })} data-testid="room-add-category">
                      <option>Deluxe</option><option>Cottage</option><option>Suite</option>
                    </select>
                  </label>
                  <label>Price per night<input value={newRoom.rate} onChange={(e) => setNewRoom({ ...newRoom, rate: e.target.value })} required placeholder="₹12,000" data-testid="room-add-rate" /></label>
                </div>
                <div className="form-grid">
                  <label>Capacity<input value={newRoom.guests} onChange={(e) => setNewRoom({ ...newRoom, guests: e.target.value })} placeholder="3 guests · 45 sq.m" data-testid="room-add-guests" /></label>
                  <label>Includes<input value={newRoom.bed} onChange={(e) => setNewRoom({ ...newRoom, bed: e.target.value })} placeholder="Breakfast for two" data-testid="room-add-bed" /></label>
                </div>
                <label>Description<textarea value={newRoom.summary} onChange={(e) => setNewRoom({ ...newRoom, summary: e.target.value })} data-testid="room-add-summary" /></label>
                <label>Amenities (comma separated)<input value={newRoom.amenities} onChange={(e) => setNewRoom({ ...newRoom, amenities: e.target.value })} placeholder="Balcony, Breakfast for two" data-testid="room-add-amenities" /></label>
                <div className="publish-row">
                  <button className="gold-button" disabled={saving} type="submit" data-testid="room-add-save">{saving ? "Adding…" : "Add room — goes live"} <ArrowUpRight size={15} /></button>
                  <button type="button" className="text-link as-button" onClick={() => setNewRoom(null)} data-testid="room-add-cancel">Cancel</button>
                </div>
              </form>
            ) : !effectiveRoom ? (
              <p className="muted">Select a room to edit its price, details and photo — or add a new one.</p>
            ) : (
              <div data-testid="room-edit-form">
                <div className="admin-card-head">
                  <h2>Edit room</h2>
                  {confirmDelete === selectedRoom.id ? (
                    <span className="publish-row"><button className="danger-btn" onClick={() => deleteRoom(selectedRoom)} data-testid="room-delete-confirm">Yes, delete permanently</button><button className="text-link as-button" onClick={() => setConfirmDelete(null)}>Keep</button></span>
                  ) : (
                    <button className="text-link as-button danger-link" onClick={() => setConfirmDelete(selectedRoom.id)} data-testid="room-delete">Delete room</button>
                  )}
                </div>
                {drafts[roomDraftKey] && <p className="draft-note" data-testid="room-draft-note">Offline draft — not on the website until you Publish.</p>}
                <label>Room name<input value={effectiveRoom.name} onChange={(e) => setDraft(roomDraftKey, { ...effectiveRoom, name: e.target.value })} required data-testid="room-edit-name" /></label>
                <div className="form-grid">
                  <label>Category
                    <select value={effectiveRoom.category} onChange={(e) => setDraft(roomDraftKey, { ...effectiveRoom, category: e.target.value })} data-testid="room-edit-category">
                      <option>Deluxe</option><option>Cottage</option><option>Suite</option>
                    </select>
                  </label>
                  <label>Price per night<input value={effectiveRoom.rate} onChange={(e) => setDraft(roomDraftKey, { ...effectiveRoom, rate: e.target.value })} required data-testid="room-edit-rate" /></label>
                </div>
                <div className="form-grid">
                  <label>Capacity<input value={effectiveRoom.guests} onChange={(e) => setDraft(roomDraftKey, { ...effectiveRoom, guests: e.target.value })} data-testid="room-edit-guests" /></label>
                  <label>Includes<input value={effectiveRoom.bed} onChange={(e) => setDraft(roomDraftKey, { ...effectiveRoom, bed: e.target.value })} data-testid="room-edit-bed" /></label>
                </div>
                <label>Description<textarea value={effectiveRoom.summary} onChange={(e) => setDraft(roomDraftKey, { ...effectiveRoom, summary: e.target.value })} data-testid="room-edit-summary" /></label>
                <label>Amenities (comma separated)<input value={effectiveRoom.amenities} onChange={(e) => setDraft(roomDraftKey, { ...effectiveRoom, amenities: e.target.value })} data-testid="room-edit-amenities" /></label>
                <div className="photo-row">
                  <div className="photo-preview" data-testid="room-photo-preview">
                    {selectedRoom.image ? <img src={selectedRoom.image} alt={selectedRoom.name} /> : <span>No photo yet</span>}
                  </div>
                  <div className="photo-actions">
                    <label className="outline-button upload-btn" data-testid="room-photo-upload-label">
                      <Upload size={14} /> {uploading ? "Uploading…" : "Upload photo"}
                      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadRoomPhoto} disabled={uploading} hidden data-testid="room-photo-upload" />
                    </label>
                    {selectedRoom.image && <button type="button" className="outline-button" onClick={removeRoomPhoto} data-testid="room-photo-remove"><Trash2 size={14} /> Remove</button>}
                    <small className="muted">Photos apply immediately. Text & price edits stay offline until Publish.</small>
                  </div>
                </div>
                <div className="publish-row">
                  <button className="gold-button" disabled={saving || !drafts[roomDraftKey]} onClick={publishRoom} data-testid="room-edit-save">{saving ? "Publishing…" : "Publish room changes"} <ArrowUpRight size={15} /></button>
                  {drafts[roomDraftKey] && <button className="text-link as-button" onClick={() => clearDraft(roomDraftKey)} data-testid="room-edit-discard">Discard draft</button>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "gallery" && (
        <div className="admin-card" data-testid="admin-gallery-panel">
          <div className="admin-card-head">
            <h2>Gallery & homepage photos</h2>
            <label className="outline-button upload-btn" data-testid="gallery-upload-label">
              <Upload size={14} /> {uploading ? "Uploading…" : "Add photo"}
              <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={uploadGalleryPhoto} disabled={uploading} data-testid="gallery-upload" />
            </label>
          </div>
          <p className="muted small">These photos run the homepage slider and Gallery page. <Star size={11} /> marks photos shown in the homepage slider. Uploads, deletes and reordering apply immediately.</p>
          <div className="gallery-admin-grid">
            {gallery.map((photo, i) => (
              <div className="gallery-admin-item" key={photo.id} data-testid={`gallery-admin-${i}`}>
                <img src={photo.image} alt={`Gallery ${i + 1}`} />
                <div className="gallery-admin-actions">
                  <button onClick={() => toggleFeatured(photo)} className={photo.featured ? "star on" : "star"} aria-label="Toggle homepage slider" data-testid={`gallery-featured-${photo.id}`}><Star size={13} /></button>
                  <button onClick={() => moveGalleryPhoto(photo, -1)} disabled={i === 0} aria-label="Move earlier" data-testid={`gallery-up-${photo.id}`}><ArrowUp size={13} /></button>
                  <button onClick={() => moveGalleryPhoto(photo, 1)} disabled={i === gallery.length - 1} aria-label="Move later" data-testid={`gallery-down-${photo.id}`}><ArrowDown size={13} /></button>
                  {confirmDelete === photo.id ? (
                    <button className="danger" onClick={() => deleteGalleryPhoto(photo)} data-testid={`gallery-delete-confirm-${photo.id}`}>Sure?</button>
                  ) : (
                    <button onClick={() => setConfirmDelete(photo.id)} aria-label="Delete photo" data-testid={`gallery-delete-${photo.id}`}><Trash2 size={13} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "settings" && eff && (
        <div className="admin-card narrow" data-testid="admin-settings-panel">
          <div className="admin-card-head">
            <h2>Site settings</h2>
            {drafts.settings && <span className="admin-badge">Unpublished offline draft</span>}
          </div>
          <p className="muted small">Edits are saved offline on this device first — press <strong>Publish settings</strong> to make them live. QR and photo uploads apply immediately.</p>

          <div className="settings-group">
            <h3>Contact</h3>
            <div className="form-grid">
              <label>Phone (display)<input value={eff.phone_display} onChange={setS("phone_display")} required data-testid="settings-phone-display" /></label>
              <label>Phone (dialling format)<input value={eff.phone_tel} onChange={setS("phone_tel")} required data-testid="settings-phone-tel" /></label>
            </div>
            <div className="form-grid">
              <label>WhatsApp number<input value={eff.whatsapp} onChange={setS("whatsapp")} required data-testid="settings-whatsapp" /></label>
              <label>Email<input type="email" value={eff.email} onChange={setS("email")} required data-testid="settings-email" /></label>
            </div>
            <label>Address<textarea value={eff.address} onChange={setS("address")} data-testid="settings-address" /></label>
            <label>Google Maps link<input value={eff.maps_url || ""} onChange={setS("maps_url")} placeholder="https://maps.google.com/…" data-testid="settings-maps" /></label>
          </div>

          <div className="settings-group">
            <h3>Payment & QR</h3>
            <div className="photo-row">
              <div className="photo-preview qr-preview" data-testid="qr-preview">
                {(settingsForm.qr_image || QR_CODE_URL) && <img src={settingsForm.qr_image || QR_CODE_URL} alt="Payment QR" />}
              </div>
              <div className="photo-actions">
                <label className="outline-button upload-btn" data-testid="qr-upload-label">
                  <Upload size={14} /> {uploading ? "Uploading…" : "Upload / replace QR"}
                  <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={uploadQr} disabled={uploading} data-testid="qr-upload" />
                </label>
                {settingsForm.qr_image && <button type="button" className="outline-button" onClick={deleteQr} data-testid="qr-delete"><Trash2 size={14} /> Delete QR</button>}
              </div>
            </div>
            <label>UPI ID<input value={eff.upi_id || ""} onChange={setS("upi_id")} placeholder="9022267126@mairtel" data-testid="settings-upi" /></label>
            <div className="form-grid">
              <label>Account holder name<input value={eff.account_name || ""} onChange={setS("account_name")} data-testid="settings-account-name" /></label>
              <label>Bank name<input value={eff.bank_name || ""} onChange={setS("bank_name")} data-testid="settings-bank-name" /></label>
            </div>
            <div className="form-grid">
              <label>Account number<input value={eff.account_number || ""} onChange={setS("account_number")} data-testid="settings-account-number" /></label>
              <label>IFSC<input value={eff.ifsc || ""} onChange={setS("ifsc")} data-testid="settings-ifsc" /></label>
            </div>
            <div className="form-grid">
              <label>Branch<input value={eff.bank_branch || ""} onChange={setS("bank_branch")} data-testid="settings-branch" /></label>
              <label>Account type<input value={eff.account_type || ""} onChange={setS("account_type")} data-testid="settings-account-type" /></label>
            </div>
            <label>Payment instructions (shown to guests)<textarea value={eff.payment_instructions || ""} onChange={setS("payment_instructions")} data-testid="settings-payment-instructions" /></label>
            <div className="toggle-grid" data-testid="payment-toggles">
              {[["pay_upi", "UPI / QR payment"], ["pay_bank", "Bank transfer"], ["pay_cash", "Cash at hotel"], ["pay_online", "Online card payment"], ["whatsapp_booking", "WhatsApp button after booking"]].map(([key, label]) => (
                <label className="toggle-row" key={key} data-testid={`toggle-${key}`}>
                  <input type="checkbox" checked={eff[key] !== false && !(key === "pay_online" && !eff[key])} onChange={setSCheck(key)} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="settings-group">
            <h3>Hotel information</h3>
            <div className="form-grid">
              <label>Hotel name<input value={eff.hotel_name || ""} onChange={setS("hotel_name")} data-testid="settings-hotel-name" /></label>
              <label>Cancellation policy (one line)<input value={eff.cancellation_policy || ""} onChange={setS("cancellation_policy")} data-testid="settings-cancellation" /></label>
            </div>
            <label>Hotel description (footer)<textarea value={eff.hotel_description || ""} onChange={setS("hotel_description")} data-testid="settings-hotel-description" /></label>
            <div className="form-grid">
              <label>Check-in time<input value={eff.checkin_time || ""} onChange={setS("checkin_time")} placeholder="2 PM" data-testid="settings-checkin" /></label>
              <label>Check-out time<input value={eff.checkout_time || ""} onChange={setS("checkout_time")} placeholder="12 PM" data-testid="settings-checkout" /></label>
            </div>
            <label>Facilities (comma separated)<input value={Array.isArray(eff.facilities) ? eff.facilities.join(", ") : ""} onChange={(e) => setDraft("settings", { ...eff, facilities: e.target.value.split(",").map((f) => f.trim()).filter(Boolean) })} data-testid="settings-facilities" /></label>
          </div>

          <div className="settings-group">
            <h3>Homepage text</h3>
            <label>Announcement bar<input value={eff.announcement || ""} onChange={setS("announcement")} data-testid="settings-announcement" /></label>
            <div className="form-grid">
              <label>Hero headline — line 1<input value={eff.hero_line1 || ""} onChange={setS("hero_line1")} data-testid="settings-hero1" /></label>
              <label>Hero headline — line 2<input value={eff.hero_line2 || ""} onChange={setS("hero_line2")} data-testid="settings-hero2" /></label>
            </div>
            <label>Hero subtitle<textarea value={eff.hero_subtitle || ""} onChange={setS("hero_subtitle")} data-testid="settings-hero-sub" /></label>
            <label>Google Ads / GTM tag ID<input value={eff.google_tag_id || ""} onChange={setS("google_tag_id")} placeholder="AW-XXXXXXXXXX or GTM-XXXXXXX" data-testid="settings-google-tag" /></label>
          </div>

          <div className="publish-row">
            <button className="gold-button" disabled={saving || !drafts.settings} onClick={publishSettings} data-testid="settings-save">{saving ? "Publishing…" : "Publish settings"} <ArrowUpRight size={15} /></button>
            {drafts.settings && <button className="text-link as-button" onClick={() => clearDraft("settings")} data-testid="settings-discard">Discard draft</button>}
          </div>
        </div>
      )}

      {tab === "backup" && (
        <div className="admin-card narrow" data-testid="admin-backup-panel">
          <h2>Backup & restore</h2>
          <p className="muted small">Download a full backup (settings, rooms, gallery, bookings, enquiries) or restore one. Restoring replaces settings/rooms/gallery with the file contents — bookings are never deleted by a restore.</p>
          <div className="publish-row">
            <a className="gold-button" href={`${API}/admin/backup`} data-testid="backup-export"><Download size={15} /> Export full backup</a>
            <a className="outline-button" href={`${API}/admin/bookings.csv`} data-testid="backup-bookings-csv">Bookings CSV</a>
            <a className="outline-button" href={`${API}/admin/enquiries.csv`} data-testid="backup-enquiries-csv">Enquiries CSV</a>
          </div>
          <div className="settings-group">
            <h3>Restore from file</h3>
            <label className="outline-button upload-btn" data-testid="backup-import-label">
              <Upload size={14} /> Import madhubhan-backup.json
              <input type="file" accept="application/json" hidden onChange={importBackup} data-testid="backup-import" />
            </label>
          </div>
        </div>
      )}
    </main>
  );
}
